import { frameShape, type Point } from 'wilderness-dom-node';
import { offset } from 'points';
import { Bezier } from 'bezier-js';
import arcToBezier from 'svg-arc-to-cubic-bezier';

import { getDistanceBetweenPoints } from 'js/shared/helpers/trig';
import { SVG_DEFAULT_STROKE_WIDTH } from 'js/config/defaults';

import { isArc, isCubicBezier, isQuadraticBezier, SVG_CURVE_TYPES } from './curves';

export type PointWithPathLength = Point & {
  pathLength: number;
};

export const getDrawCommandsFromSVGElements = (
  svgElements: NodeListOf<SVGGeometryElement>,
  minX = 0,
  minY = 0,
  viewboxScaleXFactor = 1,
  viewboxScaleYFactor = 1
) => {
  const maskPaths = [...svgElements]
    .filter(el => typeof el.getTotalLength !== 'undefined')
    .map(stroke => {
      const totalLength = stroke.getTotalLength();
      const drawCommands = frameShape(stroke);

      const points = offset(drawCommands.points, -minX, -minY)
        .map((point, pointsIndex, pointsArray): PointWithPathLength | Array<PointWithPathLength> => {
          // // Adjust points based on SVG viewbox
          point.x = point.x * viewboxScaleXFactor;
          point.y = point.y * viewboxScaleYFactor;

          // // Adjust curve points based on SVG viewbox
          if (point.moveTo) {
            return { ...point, pathLength: 0 };
          } else if (isCubicBezier(point)) {
            point.curve.x1 = point.curve.x1 * viewboxScaleXFactor;
            point.curve.x2 = point.curve.x2 * viewboxScaleXFactor;
            point.curve.y1 = point.curve.y1 * viewboxScaleYFactor;
            point.curve.y2 = point.curve.y2 * viewboxScaleYFactor;

            const bezier = new Bezier(
              pointsArray[pointsIndex - 1].x,
              pointsArray[pointsIndex - 1].y,
              point.curve.x1,
              point.curve.y1,
              point.curve.x2,
              point.curve.y2,
              point.x,
              point.y
            );

            return { ...point, pathLength: bezier.length() };
          } else if (isQuadraticBezier(point)) {
            const bezier = new Bezier(
              pointsArray[pointsIndex - 1].x,
              pointsArray[pointsIndex - 1].y,
              point.curve.x1 * viewboxScaleXFactor,
              point.curve.y1 * viewboxScaleYFactor,
              point.x * viewboxScaleXFactor,
              point.y * viewboxScaleYFactor
            );

            return { ...point, pathLength: bezier.length() };
          } else if (isArc(point)) {
            const curves = arcToBezier({
              px: pointsArray[pointsIndex - 1].x,
              py: pointsArray[pointsIndex - 1].y,
              cx: point.x * viewboxScaleXFactor,
              cy: point.y * viewboxScaleYFactor,
              rx: point.curve.rx * viewboxScaleXFactor,
              ry: point.curve.ry * viewboxScaleYFactor,
              xAxisRotation: point.curve.xAxisRotation,
              largeArcFlag: point.curve.largeArcFlag,
              sweepFlag: point.curve.sweepFlag
            });

            const convertedPoints = curves.map((curve, curveIndex, curveArr) => {
              const initialX = curveIndex === 0 ? pointsArray[pointsIndex - 1].x : curveArr[curveIndex - 1].x;
              const initialY = curveIndex === 0 ? pointsArray[pointsIndex - 1].y : curveArr[curveIndex - 1].y;

              const bezier = new Bezier(initialX, initialY, curve.x1, curve.y1, curve.x2, curve.y2, curve.x, curve.y);

              return {
                x: curve.x,
                y: curve.y,
                pathLength: bezier.length(),
                curve: {
                  type: SVG_CURVE_TYPES.CUBIC,
                  x1: curve.x1,
                  y1: curve.y1,
                  x2: curve.x2,
                  y2: curve.y2
                }
              };
            });

            return convertedPoints;
          } else {
            return { ...point, pathLength: getDistanceBetweenPoints(pointsArray[pointsIndex - 1], point) };
          }
        })
        .flatMap(p => p);

      return {
        points,
        totalLength,
        brushSize:
          (parseFloat(stroke.getAttribute('stroke-width') ?? SVG_DEFAULT_STROKE_WIDTH.toString()) ||
            SVG_DEFAULT_STROKE_WIDTH) * Math.max(viewboxScaleXFactor, viewboxScaleYFactor)
      };
    })
    .filter(path => path.totalLength > 0);

  return maskPaths;
};
